import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import API from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/lib/notify";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import {
  FileText, Upload, Trash2, Download, Plus, Search, FolderOpen,
  FileUp, Building2, User, Filter, X, RefreshCw, Info
} from "lucide-react";
import { useSortableTable } from "@/hooks/useSortableTable";
import { SortableHead } from "@/components/SortableHead";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { EmailConfirmDialog } from "@/components/EmailConfirmDialog";

const CATEGORIES = ["Onboarding", "Declarations", "Notices", "Agreements", "Financial Reports", "Other"];

const CATEGORY_COLORS = {
  Onboarding: "bg-blue-500/15 text-blue-400 border-blue-500/30",
  Declarations: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  Notices: "bg-purple-500/15 text-purple-400 border-purple-500/30",
  Agreements: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  "Financial Reports": "bg-cyan-500/15 text-cyan-400 border-cyan-500/30",
  Other: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
};

function formatFileSize(bytes) {
  if (!bytes) return "-";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
}

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
}

function getFileType(fileName) {
  if (!fileName) return "-";
  const ext = fileName.split(".").pop()?.toLowerCase();
  const typeMap = {
    pdf: "PDF",
    doc: "Word",
    docx: "Word",
    xls: "Excel",
    xlsx: "Excel",
    csv: "CSV",
    ppt: "PowerPoint",
    pptx: "PowerPoint",
    txt: "Text",
    rtf: "Rich Text",
    png: "Image",
    jpg: "Image",
    jpeg: "Image",
    gif: "Image",
    svg: "Image",
    webp: "Image",
    zip: "Archive",
    rar: "Archive",
    "7z": "Archive",
    mp4: "Video",
    mov: "Video",
    mp3: "Audio",
    wav: "Audio",
  };
  return typeMap[ext] || (ext ? ext.toUpperCase() : "-");
}

export default function DocumentsPage() {
  const { fundId: portalFundId } = useParams();
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [funds, setFunds] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const [showRequest, setShowRequest] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isFM = user?.role === "admin";
  // FM can request documents only when at least one of their funds allows it.
  // When inside a fund portal, restrict the check to that fund only.
  const fundsForGate = portalFundId ? funds.filter(f => f.id === portalFundId) : funds;
  const allowedRequestFunds = fundsForGate.filter(f => (f.fund_config?.fm_can_request_docs) !== false);
  const canRequestDocs = !isFM || allowedRequestFunds.length > 0;

  // Filters
  const [filterFund, setFilterFund] = useState(portalFundId || "all");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterScope, setFilterScope] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Replace doc state
  const [replaceTarget, setReplaceTarget] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replacing, setReplacing] = useState(false);

  // Email confirm state
  const [emailConfirm, setEmailConfirm] = useState({ open: false, action: null, description: "" });

  // Upload form
  const [uploadForm, setUploadForm] = useState({
    fund_id: "", name: "", category: "Other", scope: "fund", investor_id: "", description: "", file: null,
  });

  // Request form
  const [requestForm, setRequestForm] = useState({
    fund_id: "", investor_id: "", name: "", category: "Other", description: "",
  });

  const fetchDocuments = useCallback(async () => {
    try {
      const params = {};
      if (filterFund !== "all") params.fund_id = filterFund;
      if (filterCategory !== "all") params.category = filterCategory;
      if (filterScope !== "all") params.scope = filterScope;
      const res = await API.get("/documents", { params });
      setDocuments(res.data);
    } catch {
      toast.error("Failed to load documents");
    } finally {
      setLoading(false);
    }
  }, [filterFund, filterCategory, filterScope]);

  const fetchReferenceData = useCallback(async () => {
    try {
      const [fundsRes, investorsRes] = await Promise.all([
        API.get("/funds"),
        API.get("/investors"),
      ]);
      setFunds(fundsRes.data);
      setInvestors(investorsRes.data);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    fetchReferenceData();
  }, [fetchReferenceData]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.fund_id || !uploadForm.name) {
      toast.error("Please fill required fields and select a file");
      return;
    }
    if (uploadForm.scope === "investor" && !uploadForm.investor_id) {
      toast.error("Please select an investor");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadForm.file);
      const params = new URLSearchParams({
        fund_id: uploadForm.fund_id,
        name: uploadForm.name,
        category: uploadForm.category,
        scope: uploadForm.scope,
        description: uploadForm.description || "",
      });
      if (uploadForm.scope === "investor" && uploadForm.investor_id) {
        params.set("investor_id", uploadForm.investor_id);
      }
      await API.post(`/documents/upload?${params.toString()}`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document uploaded successfully");
      setShowUpload(false);
      setUploadForm({ fund_id: "", name: "", category: "Other", scope: "fund", investor_id: "", description: "", file: null });
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const handleRequestUpload = async () => {
    if (!requestForm.fund_id || !requestForm.investor_id || !requestForm.name) {
      toast.error("Please fill all required fields");
      return;
    }
    // Show email confirmation before sending request
    setEmailConfirm({
      open: true,
      description: "This will send an email notification to the investor requesting the document. Continue?",
      action: async () => {
        setUploading(true);
        try {
          const params = new URLSearchParams({
            fund_id: requestForm.fund_id,
            investor_id: requestForm.investor_id,
            name: requestForm.name,
            category: requestForm.category,
            description: requestForm.description || "",
          });
          await API.post(`/documents/request?${params.toString()}`);
          toast.success("Document request sent to investor");
          setShowRequest(false);
          setRequestForm({ fund_id: "", investor_id: "", name: "", category: "Other", description: "" });
          fetchDocuments();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Request failed");
        } finally {
          setUploading(false);
        }
      },
    });
  };

  const handleReplace = async () => {
    if (!replaceFile || !replaceTarget) return;
    setReplacing(true);
    try {
      const formData = new FormData();
      formData.append("file", replaceFile);
      await API.put(`/documents/${replaceTarget.id}/replace`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document replaced successfully");
      setReplaceTarget(null);
      setReplaceFile(null);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Replace failed");
    } finally {
      setReplacing(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const res = await API.get(`/documents/${doc.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.file_name || doc.name;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const handleDelete = async (doc) => {
    try {
      await API.delete(`/documents/${doc.id}`);
      toast.success("Document deleted");
      fetchDocuments();
    } catch {
      toast.error("Delete failed");
    }
  };

  const filteredDocs = documents.filter((d) => {
    // Trim leading/trailing whitespace so a stray space never causes a
    // valid query to return zero results.
    const q = (searchTerm || "").trim().toLowerCase();
    return !q || d.name.toLowerCase().includes(q) ||
      d.fund_name?.toLowerCase().includes(q) ||
      d.investor_name?.toLowerCase().includes(q);
  });

  const { sorted: sortedDocs, sortKey, sortDir, toggleSort } = useSortableTable(filteredDocs);

  const fundInvestors = investors.filter((i) =>
    uploadForm.fund_id ? i.fund_id === uploadForm.fund_id : true
  );
  const requestFundInvestors = investors.filter((i) =>
    requestForm.fund_id ? i.fund_id === requestForm.fund_id : true
  );

  const stats = {
    total: documents.length,
    fund: documents.filter((d) => d.scope === "fund").length,
    investor: documents.filter((d) => d.scope === "investor").length,
    requested: documents.filter((d) => d.status === "requested").length,
  };

  return (
    <div className="animate-fade-in" data-testid="documents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight" data-testid="documents-title">Documents</h1>
          <p className="text-muted-foreground text-sm mt-1">Manage fund and investor documents</p>
        </div>
        <div className="flex gap-2">
          {canRequestDocs && (
            <Button variant="outline" size="sm" onClick={() => setShowRequest(true)} data-testid="request-upload-btn" className="gap-1.5">
              <FileUp className="w-4 h-4" /> Request Upload
            </Button>
          )}
          <Button size="sm" onClick={() => setShowUpload(true)} data-testid="upload-doc-btn" className="gap-1.5">
            <Upload className="w-4 h-4" /> Upload Document
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        {[
          { label: "Total Documents", value: stats.total, icon: FileText, color: "text-blue-500 dark:text-blue-400", bg: "bg-blue-500/10 border-blue-500/20" },
          { label: "Fund Documents", value: stats.fund, icon: Building2, color: "text-emerald-500 dark:text-emerald-400", bg: "bg-emerald-500/10 border-emerald-500/20" },
          { label: "Investor Documents", value: stats.investor, icon: User, color: "text-amber-500 dark:text-amber-400", bg: "bg-amber-500/10 border-amber-500/20" },
          { label: "Pending Requests", value: stats.requested, icon: FileUp, color: "text-violet-500 dark:text-violet-400", bg: "bg-violet-500/10 border-violet-500/20" },
        ].map((s) => (
          <Card key={s.label} className={`stat-card ${s.bg}`} data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
            <CardContent className="p-5">
              <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{s.label}</span>
              <p className={`font-heading text-2xl font-bold mt-1 ${s.color}`}>{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            <div className="relative flex-1 min-w-[200px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search documents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-documents"
              />
            </div>
            <Select value={filterFund} onValueChange={setFilterFund}>
              <SelectTrigger className="w-[180px] h-9" data-testid="filter-fund">
                <SelectValue placeholder="All Funds" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Funds</SelectItem>
                {funds.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-[180px] h-9" data-testid="filter-category">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterScope} onValueChange={setFilterScope}>
              <SelectTrigger className="w-[140px] h-9" data-testid="filter-scope">
                <SelectValue placeholder="All Scopes" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Scopes</SelectItem>
                <SelectItem value="fund">Fund</SelectItem>
                <SelectItem value="investor">Investor</SelectItem>
              </SelectContent>
            </Select>
            {(filterFund !== "all" || filterCategory !== "all" || filterScope !== "all" || searchTerm) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterFund("all"); setFilterCategory("all"); setFilterScope("all"); setSearchTerm(""); }} className="h-9 gap-1 text-xs">
                <X className="w-3 h-3" /> Clear
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Documents Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredDocs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FolderOpen className="w-12 h-12 text-muted-foreground/50 mb-3" strokeWidth={1} />
              <p className="text-muted-foreground text-sm">No documents found</p>
              <Button size="sm" className="mt-4 gap-1.5" onClick={() => setShowUpload(true)}>
                <Upload className="w-4 h-4" /> Upload First Document
              </Button>
            </div>
          ) : (
            <Table data-testid="documents-table">
              <TableHeader>
                <TableRow>
                  <SortableHead sortKey="name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort} className="pl-6">Document</SortableHead>
                  <SortableHead sortKey="file_name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>File Type</SortableHead>
                  <SortableHead sortKey="category" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Category</SortableHead>
                  <SortableHead sortKey="scope" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Scope</SortableHead>
                  <SortableHead sortKey="fund_name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Fund</SortableHead>
                  <SortableHead sortKey="investor_name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Investor</SortableHead>
                  <SortableHead sortKey="file_size" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Size</SortableHead>
                  <SortableHead sortKey="created_at" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Date</SortableHead>
                  <SortableHead sortKey="uploaded_by_name" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Uploaded By</SortableHead>
                  <SortableHead sortKey="status" currentKey={sortKey} sortDir={sortDir} onSort={toggleSort}>Status</SortableHead>
                  <TableHead className="text-right pr-6">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedDocs.map((doc) => (
                  <TableRow key={doc.id} data-testid={`doc-row-${doc.id}`}>
                    <TableCell className="pl-6">
                      <div className="flex items-center gap-2.5">
                        <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
                        <div>
                          <p className="font-medium text-sm">{doc.name}</p>
                          {doc.description && <p className="text-xs text-muted-foreground truncate max-w-[200px]">{doc.description}</p>}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="text-[10px] font-mono tracking-wider" data-testid={`file-type-${doc.id}`}>
                        {getFileType(doc.file_name)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] ${CATEGORY_COLORS[doc.category] || CATEGORY_COLORS.Other}`}>
                        {doc.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="text-xs text-muted-foreground capitalize">{doc.scope}</span>
                    </TableCell>
                    <TableCell><span className="text-sm">{doc.fund_name || "-"}</span></TableCell>
                    <TableCell><span className="text-sm">{doc.investor_name || "-"}</span></TableCell>
                    <TableCell><span className="text-xs text-muted-foreground">{formatFileSize(doc.file_size)}</span></TableCell>
                    <TableCell><span className="text-xs text-muted-foreground">{formatDate(doc.created_at)}</span></TableCell>
                    <TableCell><span className="text-xs text-muted-foreground">{doc.uploaded_by_name || "-"}</span></TableCell>
                    <TableCell>
                      <Badge variant="outline" className={doc.status === "requested" ? "bg-amber-500/15 text-amber-400 border-amber-500/30 text-[10px]" : "bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-[10px]"}>
                        {doc.status === "requested" ? "Requested" : "Active"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right pr-6">
                      <div className="flex items-center justify-end gap-1">
                        {doc.status === "active" && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDownload(doc)} data-testid={`download-${doc.id}`}>
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setReplaceTarget(doc); setReplaceFile(null); }} data-testid={`replace-${doc.id}`} title="Replace Document">
                              <RefreshCw className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300" onClick={() => setDeleteTarget(doc)} data-testid={`delete-${doc.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Upload Document Dialog */}
      <Dialog open={showUpload} onOpenChange={setShowUpload}>
        <DialogContent className="sm:max-w-lg" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Document</DialogTitle>
            <DialogDescription>Upload a document to a fund or investor</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-blue-500/10 border border-blue-500/20 p-3 mb-2">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-blue-400">
                <p className="font-medium mb-1">Upload Guidelines</p>
                <ul className="space-y-0.5 text-blue-400/80">
                  <li>Accepted formats: PDF, DOCX, XLSX, PNG, JPG, CSV</li>
                  <li>Maximum file size: 50 MB</li>
                  <li>Use descriptive document names for easy search</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fund *</Label>
                <Select value={uploadForm.fund_id} onValueChange={(v) => setUploadForm((p) => ({ ...p, fund_id: v, investor_id: "" }))}>
                  <SelectTrigger data-testid="upload-fund-select"><SelectValue placeholder="Select fund" /></SelectTrigger>
                  <SelectContent>
                    {funds.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={uploadForm.category} onValueChange={(v) => setUploadForm((p) => ({ ...p, category: v }))}>
                  <SelectTrigger data-testid="upload-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Document Name *</Label>
              <Input value={uploadForm.name} onChange={(e) => setUploadForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g., Fund PPM Q4 2025" data-testid="upload-name-input" />
            </div>
            <div>
              <Label className="text-xs">Scope</Label>
              <Select value={uploadForm.scope} onValueChange={(v) => setUploadForm((p) => ({ ...p, scope: v }))}>
                <SelectTrigger data-testid="upload-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="fund">Fund-Level</SelectItem>
                  <SelectItem value="investor">Investor-Specific</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {uploadForm.scope === "investor" && (
              <div>
                <Label className="text-xs">Investor *</Label>
                <Select value={uploadForm.investor_id} onValueChange={(v) => setUploadForm((p) => ({ ...p, investor_id: v }))}>
                  <SelectTrigger data-testid="upload-investor-select"><SelectValue placeholder="Select investor" /></SelectTrigger>
                  <SelectContent>
                    {fundInvestors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={uploadForm.description} onChange={(e) => setUploadForm((p) => ({ ...p, description: e.target.value }))} placeholder="Optional description" rows={2} data-testid="upload-description" />
            </div>
            <div>
              <Label className="text-xs">File *</Label>
              <Input type="file" onChange={(e) => setUploadForm((p) => ({ ...p, file: e.target.files[0] }))} data-testid="upload-file-input" className="cursor-pointer" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUpload(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading} data-testid="upload-submit-btn">
              {uploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Document Dialog */}
      <Dialog open={showRequest} onOpenChange={setShowRequest}>
        <DialogContent className="sm:max-w-lg" data-testid="request-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Request Document from Investor</DialogTitle>
            <DialogDescription>The investor will be notified to upload this document</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 mb-2">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-amber-400">
                <p className="font-medium mb-1">Request Instructions</p>
                <ul className="space-y-0.5 text-amber-400/80">
                  <li>Provide a clear document name so the investor knows what to upload</li>
                  <li>Add a description with specific requirements or instructions</li>
                  <li>An email notification will be sent to the investor</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fund *</Label>
                <Select value={requestForm.fund_id} onValueChange={(v) => setRequestForm((p) => ({ ...p, fund_id: v, investor_id: "" }))}>
                  <SelectTrigger data-testid="request-fund-select"><SelectValue placeholder="Select fund" /></SelectTrigger>
                  <SelectContent>
                    {(isFM ? allowedRequestFunds : funds).map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Investor *</Label>
                <Select value={requestForm.investor_id} onValueChange={(v) => setRequestForm((p) => ({ ...p, investor_id: v }))}>
                  <SelectTrigger data-testid="request-investor-select"><SelectValue placeholder="Select investor" /></SelectTrigger>
                  <SelectContent>
                    {requestFundInvestors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs">Document Name *</Label>
              <Input value={requestForm.name} onChange={(e) => setRequestForm((p) => ({ ...p, name: e.target.value }))} placeholder="e.g., Tax Declaration 2025" data-testid="request-name-input" />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={requestForm.category} onValueChange={(v) => setRequestForm((p) => ({ ...p, category: v }))}>
                <SelectTrigger data-testid="request-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Textarea value={requestForm.description} onChange={(e) => setRequestForm((p) => ({ ...p, description: e.target.value }))} placeholder="Instructions for the investor" rows={2} data-testid="request-description" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRequest(false)}>Cancel</Button>
            <Button onClick={handleRequestUpload} disabled={uploading} data-testid="request-submit-btn">
              {uploading ? "Sending..." : "Send Request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        title="Delete Document"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This cannot be undone.`}
        confirmLabel="Delete"
        variant="destructive"
        onConfirm={() => { handleDelete(deleteTarget); setDeleteTarget(null); }}
      />

      {/* Replace Document Dialog */}
      <Dialog open={!!replaceTarget} onOpenChange={(open) => { if (!open) { setReplaceTarget(null); setReplaceFile(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="replace-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Replace Document</DialogTitle>
            <DialogDescription>Upload a new file to replace "{replaceTarget?.name}"</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-amber-500/10 border border-amber-500/20 p-3 mb-2">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-amber-400">The existing file will be permanently replaced. This action cannot be undone.</p>
            </div>
          </div>
          <div className="py-2">
            <Label className="text-xs">New File *</Label>
            <Input type="file" onChange={(e) => setReplaceFile(e.target.files[0])} data-testid="replace-file-input" className="cursor-pointer" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReplaceTarget(null); setReplaceFile(null); }}>Cancel</Button>
            <Button onClick={handleReplace} disabled={replacing || !replaceFile} data-testid="replace-submit-btn">
              {replacing ? "Replacing..." : "Replace File"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <EmailConfirmDialog
        open={emailConfirm.open}
        onOpenChange={(v) => setEmailConfirm(prev => ({ ...prev, open: v }))}
        description={emailConfirm.description}
        onConfirm={() => { if (emailConfirm.action) emailConfirm.action(); }}
      />
    </div>
  );
}
